import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { postCommentPath, postCommentUsefulPath } from "../../../../routes";
import { HelperImageFile } from "../../../shared/lib/types";
import LinkedText from "../../shared/components/ui/LinkedText";
import { Box } from "../../shared/components/ui/chakra";
import FilesWithoutImage from "../../shared/components/app/FilesWithoutImage";
import ImagesWithSlider from "../../shared/components/app/ImagesWithSlider";
import MoreMenu from "../../shared/components/app/MoreMenu";
import PostResourceAvatar from "../../shared/components/app/PostResourceAvatar";
import PostResourceDisplayName from "../../shared/components/app/PostResourceDisplayName";
import ArrowDropDownIcon from "../../shared/components/icons/ArrowDropDownIcon";
import ArrowDropUpIcon from "../../shared/components/icons/ArrowDropUpIcon";
import { SharedCurrentUser } from "../../shared/lib/types";
import useFlash from "../../shared/lib/useFlash";
import useRequest from "../../shared/lib/useRequest";
import {
  CommentFormFields,
  EditModal,
  useCommentForm,
} from "./CommentReplyForm";
import NewReplies from "./NewReplies";
import NewUsefulButton from "./NewUsefulButton";
import Reply from "./Reply";
import { PostComment, PostsShowProps } from "../lib/types";
import ReplyForm from "./ReplyForm";
import UserProfileLink from "./UserProfileLink";

import { Divider, Flex, HStack, Text, useDisclosure } from "@chakra-ui/react";

const CommentWrapper = ({
  comment: propComment,
  currentUser,
  post,
}: {
  comment: PostComment;
  currentUser: SharedCurrentUser;
  post: PostsShowProps;
}) => {
  const [comment, setComment] = useState(propComment);
  return (
    <Comment
      key={comment.updated_at}
      comment={comment}
      currentUser={currentUser}
      onUpdated={setComment}
      post={post}
    />
  );
};

const Comment = ({
  comment,
  currentUser,
  onUpdated,
  post,
}: {
  comment: PostComment;
  currentUser: SharedCurrentUser;
  onUpdated: (comment: PostComment) => void;
  post: PostsShowProps;
}) => {
  const [showReplies, setShowReplies] = useState(false);
  const [repliesCount, setRepliesCount] = useState(comment.replies_count);
  const [isFocusEndOfReplies, setIsFocusEndOfReplies] = useState(false);
  const disclosure = useDisclosure();
  const ref = useRef<HTMLDivElement>(null);
  const request = useRequest();
  const flash = useFlash();

  useEffect(() => {
    if (ref.current == null) return;

    const search = new URLSearchParams(location.search);
    if (
      comment.code == search.get("comment_code") &&
      search.has("reply_code")
    ) {
      setShowReplies(true);
    } else if (comment.code == search.get("comment_code")) {
      ref.current.scrollIntoView({ block: "start" });
      // ピッタリはみづらいので微調整
      window.scrollTo(0, window.scrollY - 64);
    }
    // 初回のみ実行したいので
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReply = async () => {
    const res = await request(postCommentPath(comment.code), "GET");
    if (res.ok) {
      const comment = await res.json() as PostComment;
      setShowReplies(true);
      setRepliesCount(comment.replies_count);
      setIsFocusEndOfReplies(true);
      onUpdated(comment);
    } else {
      flash({
        error: "回答が見つかりませんでした。ページを再読み込みしてください。",
      });
    }
  };

  return (
    <Box>
      <Box
        ref={ref}
        bg="white"
        px={{ base: 5, md: 9 }}
        py={{ base: 5, md: 7 }}
        borderRadius={8}
        border="1px solid var(--chakra-colors-border-cardOutline)"
      >
        <Flex justifyContent="space-between">
          <HStack gap={3}>
            <UserProfileLink author={comment.author} currentUser={currentUser}>
              <PostResourceAvatar author={comment.author} ignoreFallback />
            </UserProfileLink>
            <Box>
              <UserProfileLink
                author={comment.author}
                currentUser={currentUser}
              >
                <PostResourceDisplayName author={comment.author} />
              </UserProfileLink>
              <HStack columnGap={4} rowGap={0} wrap="wrap">
                <Box fontSize="xs" color="#848484">
                  投稿日：{dayjs(comment.created_at).format("L LT")}
                </Box>
                {comment.updated_at !== "" &&
                  comment.created_at !== comment.updated_at && (
                    <Box fontSize="xs" color="#848484">
                      最終更新日：{dayjs(comment.updated_at).format("L LT")}
                    </Box>
                  )}
              </HStack>
            </Box>
          </HStack>
          {!post.is_resolved && comment.is_own && (
            <MoreMenu
              buttonProps={{
                "data-testid": "post-comment-detail-button",
              }}
              listItemProps={{
                as: "button",
                textAlign: "left",
                onClick: disclosure.onOpen,
              }}
              listItemText="回答を編集"
            />
          )}
        </Flex>
        <Box wordBreak="break-word" whiteSpace="pre-wrap" w="full" mt={4}>
          <LinkedText>{comment.content}</LinkedText>
        </Box>
        {comment.files_without_image.length !== 0 && (
          <Box mt={5}>
            <FilesWithoutImage files={comment.files_without_image} />
          </Box>
        )}
        {comment.image_files.length !== 0 && (
          <Box mt={5}>
            <ImagesWithSlider
              urls={comment.image_files.map(
                (file: HelperImageFile) => file.url,
              )}
            />
          </Box>
        )}
        <Flex justifyContent="right" mt={4}>
          <NewUsefulButton
            useful={comment.is_marked_useful}
            count={comment.useful_count}
            path={postCommentUsefulPath(comment.code)}
            isDisabled={comment.is_own}
          />
        </Flex>
        {currentUser?.is_all_public_feature_accessible &&
          comment.first_reply && (
            <Box>
              <Divider
                borderBottomWidth="0.5px"
                borderColor="var(--chakra-colors-borderDivider)"
                mt={6}
              />
              <Box as="section" mt={6}>
                <Box as="h3" fontWeight="bold">
                  返信
                </Box>
                <Box mt={6}>
                  <Reply
                    reply={comment.first_reply}
                    currentUser={currentUser}
                    post={post}
                  />
                </Box>
              </Box>
            </Box>
          )}
        {repliesCount > 1 && (
          <>
            <Divider
              borderBottomWidth="0.5px"
              borderStyle="dashed"
              borderColor="var(--chakra-colors-primary)"
              mt={6}
            />
            <HStack
              gap={2}
              fontSize="sm"
              color="gray.base"
              alignItems="baseline"
              as="button"
              mt={6}
              onClick={() => {
                setIsFocusEndOfReplies(false);
                setShowReplies(!showReplies);
              }}
              role="group"
              sx={{
                "&:hover span": {
                  textDecoration: "underline",
                },
                "&:hover .no-underline": {
                  textDecoration: "none",
                },
              }}
            >
              <Box>
                <Box as="span">その他の返信</Box>
              </Box>
              <Box>
                <Box
                  as="span"
                  fontSize="xl"
                  fontWeight="bold"
                  color="textBase"
                  className="no-underline"
                >
                  {repliesCount - 1}
                </Box>
                件
              </Box>
              <Box>
                <Box as="span">を表示する</Box>
                {showReplies ? (
                  <ArrowDropUpIcon boxSize={6} />
                ) : (
                  <ArrowDropDownIcon boxSize={6} />
                )}
              </Box>
            </HStack>
            {showReplies && currentUser?.is_all_public_feature_accessible && (
              <NewReplies
                comment={comment}
                currentUser={currentUser}
                onFetched={(result) => setRepliesCount(result.replies_count)}
                isFocusEndOfReplies={isFocusEndOfReplies}
                post={post}
              />
            )}
          </>
        )}
        <Divider borderBottomWidth="0.5px" borderColor="borderDivider" mt={6} />
        <Box mt={6}>
          {post.is_resolved ? (
            <Text
              align="center"
              fontSize="sm"
              color="#6D787D"
              wordBreak="keep-all"
              overflowWrap="anywhere"
            >
              解決済みの質問のため、
              <wbr />
              返信できません
            </Text>
          ) : (
            <ReplyForm
              post={post}
              currentUser={currentUser}
              comment={comment}
              onSubmitted={async () => {
                await onReply();
              }}
            />
          )}
        </Box>
      </Box>
      <CommentEditModal
        comment={comment}
        onUpdated={onUpdated}
        disclosure={disclosure}
        currentUser={currentUser}
      />
    </Box>
  );
};

const CommentEditModal = ({
  comment,
  onUpdated,
  disclosure,
  currentUser,
}: {
  comment: PostComment;
  onUpdated: (comment: PostComment) => void;
  disclosure: ReturnType<typeof useDisclosure>;
  currentUser: SharedCurrentUser;
}) => {
  const request = useRequest();
  const showFlash = useFlash();

  const commentFormState = useCommentForm({
    onSubmit: async (data) => {
      const res = await request(postCommentPath(comment.code), "PUT", data);
      if (res.ok) {
        onUpdated(await res.json() as PostComment);
      } else if (res.status === 404) {
        showFlash({
          error: (
            <>
              回答が見つかりませんでした。
              <br />
              ページを再読み込みしてください。
            </>
          ),
        });
      }
    },
    defaultValues: { content: comment.content },
    defaultImageFiles: comment.image_files,
    defaultFilesWithoutImage: comment.files_without_image,
  });
  return (
    <EditModal
      isOpen={disclosure.isOpen}
      onClose={disclosure.onClose}
      formState={commentFormState}
      title="回答編集"
    >
      <CommentFormFields
        formState={commentFormState}
        currentUser={currentUser}
        anonymous={comment.author.anonymous}
      />
    </EditModal>
  );
};

export default CommentWrapper;
